import { pause } from './schedulers';

// Same bound as upstream `API_DESTROY_TIMEOUT_MS` (telegram-tt global/actions/api/initial.ts)
export const SIGN_OUT_NETWORK_TIMEOUT_MS = 3000;

/**
 * Runs a best-effort sign-out step: never throws and never waits longer than `timeoutMs`.
 * After a broken auth key (-404) the main connection is dead and any RPC may hang forever,
 * so network steps must not block local cleanup (SPAC-2771).
 */
export async function runSignOutStep(
  step: () => Promise<unknown> | unknown,
  timeoutMs = SIGN_OUT_NETWORK_TIMEOUT_MS,
) {
  try {
    await Promise.race([
      Promise.resolve().then(step),
      pause(timeoutMs),
    ]);
  } catch (err) {
    // Do nothing
  }
}

export type SignOutTarget = {
  slot: number;
  forLogin: boolean;
};

/**
 * Where to go after sign-out: another logged-in account, or the login screen (`forLogin`).
 * `loggedInSlots` must be computed AFTER the current slot session is cleared.
 *
 * The login screen is always opened with `#login`, so `App.tsx` does not auto-redirect away from slot 1,
 * and on slot 1 when it is empty, since `Auth.tsx` / `authorizationStateReady` on slot 2+ bounce there anyway.
 * A plain reload of slot 1 without `#login` could redirect to another slot that bounces back (SPAC-2771 loop).
 */
export function getSignOutTarget({
  currentSlot,
  loggedInSlots,
  isSessionBroken,
}: {
  currentSlot: number;
  loggedInSlots: number[];
  isSessionBroken?: boolean;
}): SignOutTarget {
  const otherSlots = loggedInSlots.filter((slot) => slot !== currentSlot).sort((a, b) => a - b);

  // Regular sign-out switches to the next logged-in account. A broken session (the server no longer knows it)
  // stays on the login screen, so the user can re-login and gets a code
  if (!isSessionBroken) {
    const nextSlot = otherSlots.find((slot) => slot > currentSlot) ?? otherSlots[0];
    if (nextSlot) return { slot: nextSlot, forLogin: false };
  }

  const slot = currentSlot === 1 || otherSlots.includes(1) ? currentSlot : 1;
  return { slot, forLogin: true };
}
